import api from '../clients/api';
import { addInlineTableStyles } from '../components/utils/addInlineTableStyles';
import { createPrintHTMLDoc } from '../components/utils/createPrintHTMLDoc';
import services from '../services';
import { notifyBugsnagHandledError } from './bugsnagClient';

/**
 * Triggers a browser download for a file that already has a URL
 * @param fileUrl - The URL of the file to download
 * @param name - The name of the downloaded file (including extension)
 */
export const downloadFile = (fileUrl: string, name: string) => {
	const link = document.createElement('a');
	link.href = fileUrl;
	link.download = name;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Triggers a browser download for a Blob by wrapping it in a temporary object URL
 * @param blob - The Blob to download
 * @param filename - The name of the downloaded file (including extension)
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	try {
		downloadFile(url, filename);
	} finally {
		URL.revokeObjectURL(url);
	}
};

/**
 * Rasterizes an SVG element to a PNG data URL at the given dimensions
 * @param svg - The SVG element to convert
 * @param width - The width of the resulting PNG
 * @param height - The height of the resulting PNG
 */
export const convertSvgToPng = (
	svg: SVGSVGElement,
	width: number,
	height: number
): Promise<string> => {
	return new Promise((resolve, reject) => {
		const svgSerialization = new XMLSerializer().serializeToString(svg);
		const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgSerialization);

		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			reject('Failed to get 2D context');
			return;
		}

		const img = new Image();
		img.src = svgBase64;

		img.onload = () => {
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			const png = canvas.toDataURL('image/png');
			resolve(png);
		};

		img.onerror = () => {
			reject('Error loading SVG data');
		};
	});
};

/**
 * Downloads HTML content as a PDF file
 * @param html - The HTML content to convert and download
 * @param filename - The name of the file (without extension)
 * @param accessToken - The public access token for the document
 */
export const downloadHtmlAsPdf = async (
	html: string,
	filename: string,
	accessToken?: string
): Promise<void> => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const blob = await services.pdf.convertHtmlToPdfBlob(styledHtml, accessToken);
		triggerBlobDownload(blob, `${filename}.pdf`);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

/**
 * Downloads HTML content as a .docx file
 * @param html - The HTML content to convert and download
 * @param filename - The name of the file (without extension)
 */
export const downloadHtmlAsDocx = async (html: string, filename: string): Promise<void> => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const blob = await api.document.convertHtmlToDocx(styledHtml);
		triggerBlobDownload(blob, `${filename}.docx`);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

/**
 * Opens a print dialog with HTML content
 * @param title - The title of the document
 * @param html - The HTML content to print
 */
export const printHtmlDocument = (title: string, html: string): void => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const printDocument = createPrintHTMLDoc(title, styledHtml);

		// Open HTML in a new window and print
		const printWindow = window.open('', '_blank');
		if (printWindow) {
			printWindow.document.write(printDocument);
			printWindow.document.close();

			// Wait for content to load, then trigger print
			printWindow.onload = () => {
				setTimeout(() => {
					printWindow.print();
				}, 250);
			};
		}
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};
